import React, { useContext } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import PortableText from 'react-portable-text';
import loadable from '@loadable/component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Button,
} from '@material-ui/core';
import { BgImage } from 'gbimage-bridge';

import FormModalContext from '../../context/FormModalContext';

const Video = loadable(() => import('../Home/Hero/Video'));

const useStyles = makeStyles((theme) => ({
  '@keyframes pulseBorder': {
    '0%': {
      transform: 'translateX(-50%) translateY(-50%) translateZ(0) scale(1)',
      opacity: '1',
    },
    '100%': {
      transform: 'translateX(-50%) translateY(-50%) translateZ(0) scale(1.5)',
      opacity: '0',
    },
  },
  imgCont: {
    padding: '2rem 0 2rem 0',
    maxWidth: '100vw',
    height: '100%',
    overflow: 'visible',
    backgroundSize: 'cover',
    background:
      'linear-gradient(0deg, rgba(248, 251, 255, 0.8), rgba(248, 251, 255, 0.8)), linear-gradient(0deg, #F8FBFF, #F8FBFF), #F8FBFF',
    [theme.breakpoints.down('md')]: {},
    [theme.breakpoints.down('sm')]: {
      paddingTop: '3rem',
      backgroundPosition: 'right',
    },
    [theme.breakpoints.down('xs')]: {
      padding: '3rem 0',
    },
  },

  root: {
    width: '100%',
    maxWidth: '100vw',
    [theme.breakpoints.down('xs')]: {
      paddingTop: '2rem',
      paddingBottom: '2rem',
    },
  },
  playButton: {
    height: '100px',
    width: '100px',
    minWidth: '100px',
    background: theme.white,
    color: theme.workwaveBlue,
    fontSize: '1.5rem',
    borderRadius: '50%',
    opacity: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow:
      '0 10px 25px rgba(50, 50, 93, 0.25), 0 6px 10px rgba(0, 0, 0, 0.15)',
    cursor: 'pointer',
    zIndex: 5,

    '&::before': {
      content: `''`,
      position: 'absolute',
      zIndex: '-1',
      left: '50%',
      top: '50%',
      transform: 'translateX(-50%) translateY(-50%)',
      display: 'block',
      width: '100px',
      height: '100px',
      background: theme.white,
      borderRadius: '50%',
    },

    '&:hover': {
      '& $playButtonEffect': { transform: 'scale(1.1)' },
      '&::before': {
        animation: '$pulseBorder 1500ms ease-out infinite',
      },
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  playButtonEffect: {
    padding: '130px',
    position: 'absolute',
    marginTop: '2rem',
    marginBottom: '-2rem',
    borderRadius: '20px',
    zIndex: 5,
    transition: 'all 1s ease-in-out',
    height: '600px',
    width: '380px',
    boxShadow: '0 10px 40px 0px rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  text: {
    color: theme.workwaveBlue,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  header: {
    lineHeight: '1.1 !important',
    color: '#FFF',
    [theme.breakpoints.down('xs')]: {
      fontSize: '2.2rem',
    },
  },
  heroSubheader: {
    color: '#FFF',
    letterSpacing: '.2px',
    lineHeight: '53px',
    fontWeight: 700,
    fontSize: '56px',
    [theme.breakpoints.down('md')]: {
      fontSize: '42px',
      lineHeight: '40px',
    },
  },
  heroBody: {
    color: '#FFF',
    paddingTop: '1.25rem',
    paddingBottom: '.5rem',
    fontFamily: 'Roboto',
    fontWeight: 400,
    fontSize: '1.375rem',
    lineHeight: '30px',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.125rem',
    },
  },
  heroContent: {
    position: 'relative',
    lineHeight: '28px',
    fontSize: '32px',
    color: '#F8FBFF',
    marginTop: '1rem',
    fontWeight: 400,
    textAlign: 'left',
    [theme.breakpoints.down('md')]: {
      fontSize: '30px',
    },
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      fontSize: '22px',
      fontWeight: 600,
      left: 0,
    },
  },
  heroButton: {
    padding: '2rem 0 3rem 0',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 0,
    },
  },
  playerWrapper: {
    position: 'relative',
    paddingTop: '56.25%',
    minHeight: '100%',
    minWidth: '100%',
    border: '1px solid #d8dee0',
    marginTop: '.5rem',
  },
  reactPlayer: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  nameTitleImage: {
    position: 'absolute',
    left: '-45px',
    bottom: '65px',
    zIndex: 9999,
  },
  trademark: {
    fontSize: '1.5rem',
  },
}));

export const HeroVideoEmbed = ({ hero, setHeroModalOpen }) => {
  const classes = useStyles();

  const { setFormModalOpen } = useContext(FormModalContext);

  const md = useMediaQuery('(max-width: 960px)');
  return (
    <BgImage
      image={hero.backgroundImage?.asset?.gatsbyImageData}
      className={classes.imgCont}
    >
      <Grid
        container
        direction='row'
        alignItems='center'
        className={classes.root}
      >
        <Container style={{ position: 'relative' }}>
          <Grid container alignItems='center'>
            <Grid
              container
              item
              direction='column'
              justifyContent='center'
              alignItems={md ? 'center' : 'flex-start'}
              className={classes.heroContent}
              xs={12}
              md={8}
            >
              {hero._rawContent.map((content, index) => (
                <PortableText
                  key={index}
                  content={content}
                  className={classes.text}
                  serializers={{
                    h1: ({ children }) => (
                      <Grid item md={11} lg={12}>
                        <Typography variant='h1' className={classes.header}>
                          {children}
                        </Typography>
                      </Grid>
                    ),
                    h4: ({ children }) => (
                      <Typography
                        variant='h4'
                        className={classes.heroSubheader}
                      >
                        {children}
                      </Typography>
                    ),
                    normal: ({ children }) => {
                      return (
                        <Grid item xs={12} sm={11} md={10}>
                          <Typography
                            variant='body1'
                            className={classes.heroBody}
                          >
                            {children}
                          </Typography>
                        </Grid>
                      );
                    },
                    sup: ({ children }) => (
                      <sup className={classes.trademark}>
                        {children}
                      </sup>
                    ),
                  }}
                />
              ))}{' '}
              {!!hero.ctaText && (
                <Button
                  color='secondary'
                  size='large'
                  variant='contained'
                  onClick={() => setFormModalOpen(true)}
                  style={{ marginTop: '2rem' }}
                >
                  {hero.ctaText}
                </Button>
              )}
            </Grid>
            <Grid
              item
              md={4}
              container
              alignItems='center'
              justifyContent='flex-end'
            >
              <div
                className={classes.playButtonEffect}
                style={{
                  background: `url(${hero.wistiaImage?.asset?.gatsbyImageData.images.fallback.src})`,
                  position: 'relative', // Ensure relative positioning for absolute positioning of the play button
                }}
              >
                <GatsbyImage
                  image={hero.nameAndTitleImage?.asset?.gatsbyImageData}
                  alt='Joe King, Chief Revenue Officer'
                  className={classes.nameTitleImage}
                  quality={100}
                />
                <div
                  className={classes.playButton}
                  onClick={(e) => setHeroModalOpen(true)}
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'play']} size='lg' />
                </div>
              </div>
            </Grid>
          </Grid>
          {md && (
            <Grid
              container
              direction='row'
              justifyContent='center'
              alignItems='center'
              style={{ marginTop: '2rem' }}
              item
              xs
            >
              <div className={classes.playerWrapper}>
                <Video
                  url={hero.wistiaLink}
                  height='100%'
                  width='100%'
                  controls={true}
                />
              </div>
            </Grid>
          )}
        </Container>
      </Grid>
    </BgImage>
  );
};
